import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { CheckCircle2, X } from 'lucide-react';
import EscalationTimeline from '../escalation/EscalationTimeline';
import { useCriticalNotificationDetail } from '../../hooks/useCriticalNotificationDetail';
import type { CriticalNotificationDetailContext } from '../../types/criticalNotification.types';
import {
  acknowledgementStatusClass,
  acknowledgementStatusLabel,
  canAcknowledgeNotification,
  formatNotificationTimestamp,
  notificationStatusClass,
  notificationStatusLabel,
  resolveNotificationAccessionLabel,
  resolveNotificationOrderingClinician,
  resolveNotificationPatientName,
  resolveNotificationStudyLabel,
} from '../../utils/criticalNotificationUtils';
import { formatCriticalityLabel } from '../../utils/criticalResultsDashboardUtils';
import { ICON_STROKE_WIDTH } from '../../constants';
import '../../pages/admin/EscalationMonitor.css';
import '../../pages/admin/CriticalResultsDashboard.css';

interface CriticalNotificationDetailModalProps {
  open: boolean;
  context: CriticalNotificationDetailContext | null;
  onClose: () => void;
  onAcknowledged?: () => void;
}

const HISTORY_TABS = [
  { id: 'timeline' as const, label: 'Timeline' },
  { id: 'reminders' as const, label: 'Reminder History' },
  { id: 'escalation' as const, label: 'Escalation History' },
];

function DetailField({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <div className="crd-notification-detail-field">
      <span className="crd-notification-detail-label">{label}</span>
      <div className="crd-notification-detail-value">{children}</div>
    </div>
  );
}

function resolvePatientVisitId(
  context: CriticalNotificationDetailContext | null,
  notification: ReturnType<typeof useCriticalNotificationDetail>['notification']
): string | null {
  return (
    notification?.patient?.risVisitId?.trim() ||
    context?.risVisitId?.trim() ||
    (context?.accessionLabel?.trim() !== '—' ? context?.accessionLabel?.trim() : null) ||
    null
  );
}

function acknowledgedByLabel(
  notification: NonNullable<ReturnType<typeof useCriticalNotificationDetail>['notification']>
): string {
  return (
    notification.acknowledgedByUser?.fullName ||
    notification.acknowledgedByUser?.username ||
    notification.acknowledgedByContact?.name ||
    '—'
  );
}

function historyEmptyMessage(activeTab: string): string {
  if (activeTab === 'reminders') {
    return 'No reminder events recorded for this notification.';
  }
  if (activeTab === 'escalation') {
    return 'No escalation events recorded for this notification.';
  }
  return 'No notification history recorded yet.';
}

function NotificationDetailSummary({
  notification,
  patientName,
  patientVisitId,
  orderingClinician,
}: Readonly<{
  notification: NonNullable<ReturnType<typeof useCriticalNotificationDetail>['notification']>;
  patientName: string;
  patientVisitId: string | null;
  orderingClinician: string;
}>) {
  return (
    <section
      className="crd-notification-detail-summary"
      aria-label="Notification details"
    >
      <DetailField label="Patient">
        {patientName}
        {patientVisitId ? ` · ${patientVisitId}` : ''}
      </DetailField>
      <DetailField label="Ordering clinician">{orderingClinician}</DetailField>
      <DetailField label="Criticality">
        {formatCriticalityLabel(String(notification.criticalityBand))}
      </DetailField>
      <DetailField label="Notification status">
        <span className={notificationStatusClass(notification.notificationStatus)}>
          {notificationStatusLabel(notification.notificationStatus)}
        </span>
      </DetailField>
      <DetailField label="Acknowledgement status">
        <span className={acknowledgementStatusClass(notification.acknowledgementStatus)}>
          {acknowledgementStatusLabel(notification.acknowledgementStatus)}
        </span>
      </DetailField>
      <DetailField label="Acknowledged at">
        {formatNotificationTimestamp(notification.acknowledgedAt)}
      </DetailField>
      <DetailField label="Acknowledged by">{acknowledgedByLabel(notification)}</DetailField>
      <DetailField label="Current level">
        {notification.escalationInstance?.currentLevelNumber
          ? `L${notification.escalationInstance.currentLevelNumber}`
          : '—'}
      </DetailField>
    </section>
  );
}

function NotificationDetailBody({
  detailLoading,
  detailError,
  notification,
  patientName,
  patientVisitId,
  orderingClinician,
  ackError,
  activeTab,
  setActiveTab,
  historyError,
  historyLoading,
  activeEvents,
}: Readonly<{
  detailLoading: boolean;
  detailError: string | null;
  notification: ReturnType<typeof useCriticalNotificationDetail>['notification'];
  patientName: string;
  patientVisitId: string | null;
  orderingClinician: string;
  ackError: string | null;
  activeTab: string;
  setActiveTab: (tab: (typeof HISTORY_TABS)[number]['id']) => void;
  historyError: string | null;
  historyLoading: boolean;
  activeEvents: ReturnType<typeof useCriticalNotificationDetail>['activeEvents'];
}>) {
  const renderSummary = () => {
    if (detailLoading) {
      return <p className="esc-monitor-placeholder">Loading notification details…</p>;
    }
    if (detailError) {
      return (
        <p
          className="crd-error"
          role="alert"
        >
          {detailError}
        </p>
      );
    }
    if (!notification) {
      return null;
    }
    return (
      <NotificationDetailSummary
        notification={notification}
        patientName={patientName}
        patientVisitId={patientVisitId}
        orderingClinician={orderingClinician}
      />
    );
  };

  const renderHistory = () => {
    if (historyLoading) {
      return <p className="esc-monitor-placeholder">Loading notification history…</p>;
    }
    if (activeEvents.length === 0) {
      return <p className="esc-monitor-placeholder">{historyEmptyMessage(activeTab)}</p>;
    }
    return (
      <EscalationTimeline
        events={activeEvents}
        emptyMessage="No events recorded."
      />
    );
  };

  return (
    <div className="esc-monitor-modal-body crd-notification-detail-body">
      {renderSummary()}
      {ackError ? (
        <p
          className="crd-error"
          role="alert"
        >
          {ackError}
        </p>
      ) : null}
      <div
        className="crd-notification-history-tabs"
        role="tablist"
        aria-label="Notification history"
      >
        {HISTORY_TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`crd-notification-history-tab${
              activeTab === tab.id ? 'crd-notification-history-tab--active' : ''
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {historyError ? (
        <p
          className="crd-error"
          role="alert"
        >
          {historyError}
        </p>
      ) : null}
      {renderHistory()}
    </div>
  );
}

export default function CriticalNotificationDetailModal({
  open,
  context,
  onClose,
  onAcknowledged,
}: Readonly<CriticalNotificationDetailModalProps>) {
  const {
    notification,
    activeTab,
    setActiveTab,
    activeEvents,
    detailLoading,
    historyLoading,
    acknowledging,
    detailError,
    historyError,
    ackError,
    handleAcknowledge,
  } = useCriticalNotificationDetail({
    open,
    context,
    onAcknowledged,
  });

  const title = resolveNotificationAccessionLabel(context, notification);
  const subtitle = resolveNotificationStudyLabel(context, notification);
  const patientName = resolveNotificationPatientName(context, notification);
  const orderingClinician = resolveNotificationOrderingClinician(context, notification);
  const patientVisitId = resolvePatientVisitId(context, notification);
  const showAcknowledge = canAcknowledgeNotification(notification);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={isOpen => !isOpen && onClose()}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="esc-monitor-modal-overlay" />
        <Dialog.Content className="esc-monitor-modal crd-notification-detail-modal">
          <header className="esc-monitor-modal-header">
            <div>
              <Dialog.Title className="esc-monitor-modal-title">{title}</Dialog.Title>
              {subtitle ? (
                <Dialog.Description className="esc-monitor-modal-subtitle">
                  {subtitle}
                </Dialog.Description>
              ) : null}
            </div>
            <div className="esc-monitor-modal-actions">
              {showAcknowledge ? (
                <button
                  type="button"
                  className="esc-btn esc-btn--primary esc-btn--inline"
                  disabled={acknowledging || detailLoading}
                  onClick={() => void handleAcknowledge()}
                >
                  <CheckCircle2
                    size={16}
                    strokeWidth={ICON_STROKE_WIDTH}
                  />
                  {acknowledging ? 'Acknowledging…' : 'Acknowledge'}
                </button>
              ) : null}
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="esc-monitor-modal-close"
                  aria-label="Close"
                >
                  <X
                    size={18}
                    strokeWidth={ICON_STROKE_WIDTH}
                  />
                </button>
              </Dialog.Close>
            </div>
          </header>

          <NotificationDetailBody
            detailLoading={detailLoading}
            detailError={detailError}
            notification={notification}
            patientName={patientName}
            patientVisitId={patientVisitId}
            orderingClinician={orderingClinician}
            ackError={ackError}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            historyError={historyError}
            historyLoading={historyLoading}
            activeEvents={activeEvents}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
